import React from "react";

import {
  Autocomplete,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

const filter = createFilterOptions<string>();

export interface ComboboxProps {
  /** Current value (a plain string — freeSolo). */
  value?: string;
  /** Emitted on every change: option selected, "Add" chosen, or free text typed. */
  onChange?: (value: string) => void;
  /** Existing options to suggest. */
  options?: string[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  name?: string;
  onBlur?: () => void;
  /** Label for the "create new" row. Defaults to `Add "<input>"`. */
  addLabel?: (input: string) => string;
}

const ADD_RE = /^Add "(.*)"$/;

/**
 * Creatable autocomplete (combobox). Shows existing `options`; if the typed
 * value isn't in the list, surfaces an "Add …" row so the user can create it.
 * Value is always a plain string (freeSolo), so it drops straight into a
 * react-hook-form Controller. Styling mirrors the @repo/ui Input (neutral rest
 * border + soft brand focus ring, flat dark-mode dropdown).
 */
export const Combobox = ({
  value = "",
  onChange,
  options = [],
  placeholder,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  name,
  onBlur,
  addLabel = (input) => `Add "${input}"`,
}: ComboboxProps) => {
  const theme = useTheme();
  const brand = theme.palette.brand?.[600] || "#7F48DF";

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      handleHomeEndKeys
      disabled={disabled}
      fullWidth={fullWidth}
      // freeSolo string field: drive everything off inputValue so a value typed
      // and committed by Save (without pressing Enter) is still captured.
      inputValue={value}
      options={options}
      onInputChange={(_, next) => {
        const m = next.match(ADD_RE);
        onChange?.(m ? m[1] : next);
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const input = params.inputValue.trim();
        const exists = opts.some(
          (o) => o.toLowerCase() === input.toLowerCase()
        );
        if (input !== "" && !exists) {
          filtered.push(addLabel(input));
        }
        return filtered;
      }}
      slotProps={{
        paper: {
          sx: {
            // Flat dark-mode surface (kill MUI elevation overlay), consistent
            // with the theme-level MuiMenu override.
            backgroundImage: "none",
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 3,
            // Gap between the trigger and the popover menu — the house 8px, the same
            // value MENU_PAPER_SX carries for real Menu papers. Absolute px, not the
            // `mt: 2` this was: layer 1 renders under both apps' themes and `2` means
            // 8px in one and 16px in the other (§47).
            mt: '8px',
            // Match the paper radius to the option radius (8px) so the container
            // corners aren't tighter than the items (MUI default is 4px).
            borderRadius: "8px",
            // Inset the listbox so the rounded option highlight has breathing room
            // (matches the @repo/ui Select dropdown).
            "& .MuiAutocomplete-listbox": {
              padding: "8px",
            },
            // Options styled like the Select component: 16px font, rounded.
            // Design-system rule: hover = neutral grey (action.hover); brand tint
            // is reserved for the SELECTED value, not hover.
            "& .MuiAutocomplete-option": {
              fontSize: "16px",
              borderRadius: "8px",
              // Match the Select component item metrics: 12px padding, 48px min-height,
              // 4px gap between items (was MUI default 6px/16px, 44px).
              padding: "12px",
              minHeight: "48px",
              marginBottom: "4px",
              '&:last-of-type': { marginBottom: 0 },
              '&:hover, &.Mui-focusVisible, &[data-focus="true"]': {
                backgroundColor: theme.palette.action.hover,
              },
              '&[aria-selected="true"]': {
                backgroundColor: alpha(brand, 0.16),
              },
              '&[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focusVisible': {
                backgroundColor: alpha(brand, 0.16),
              },
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          variant="filled"
          fullWidth={fullWidth}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            sx: {
              borderRadius: "8px",
              backgroundColor: theme.palette.primary?.[50] || "#f6f6f6",
              border: `1px solid ${theme.palette.primary?.[100] || "#e7e7e7"}`,
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              // Match the standard @repo/ui Input (16px / 12px padding / 49px tall).
              // Kill the filled-variant label inset on the root, and override MUI's
              // Autocomplete input padding (which ships with !important) so only the
              // 12px we want applies.
              // MUI's .MuiAutocomplete-inputRoot ships a ~19px label inset on the
              // root; !important is needed to beat it (plain sx loses on specificity).
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              "& .MuiAutocomplete-input": {
                fontSize: "16px",
                padding: "12px !important",
              },
              "&:hover": {
                backgroundColor: theme.palette.primary?.[50] || "#f6f6f6",
                borderColor: disabled ? undefined : brand,
              },
              "&.Mui-focused": {
                backgroundColor: theme.palette.primary?.[50] || "#f6f6f6",
                borderColor: brand,
                boxShadow: `0 0 0 3px ${theme.palette.brand?.["600-6%"] || alpha(brand, 0.16)}`,
              },
            },
          }}
        />
      )}
    />
  );
};
